import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Layers,
  PoundSterling,
  FileText,
  SlidersHorizontal,
  BarChart3,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/estimating-software-for-builders";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Estimating Software for Builders",
  "description": "Estimating software for builders. Accurate cost build-ups from drawings and descriptions with quantity-based estimates, UK labour and materials rates, and 31 trade categories.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Estimating Software for Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best estimating software for builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential builders, The Proper Tool is purpose-built estimating software: a 31-category UK cost library with 462 rated tasks, AI-generated quantity-based cost build-ups from drawings or descriptions, labour/materials/plant splits, and professional PDF output. Plans start at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "What is building estimating software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Building estimating software automates the process of producing a cost estimate for a construction project. It combines a database of UK labour, materials, and plant rates with a process for deriving quantities from drawings or descriptions. The result is a fully itemised cost build-up that reflects the actual scope of the project. The Proper Tool is building estimating software designed specifically for UK residential construction.",
      },
    },
    {
      "@type": "Question",
      "name": "Can The Proper Tool produce quantity-based estimates from drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit (m², m, nr), and unit rate. When you upload architect's drawings or structural plans, the tool extracts floor areas, beam sizes, spans, and dimensions, using them to derive quantities more accurately than a description alone can achieve.",
      },
    },
    {
      "@type": "Question",
      "name": "Does building estimating software include labour and materials costs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Every line item in The Proper Tool carries a labour cost, materials cost, and plant cost breakdown. The split is derived from a 31-category rule set calibrated to UK residential construction norms. You can see the breakdown per item and per trade section, and the builder can toggle whether the breakdown is visible on the client-facing PDF.",
      },
    },
    {
      "@type": "Question",
      "name": "How does estimating software for builders differ from quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Estimating software focuses on the cost build-up: deriving accurate quantities, applying labour and materials rates, and producing a detailed itemised estimate. Quoting software focuses on the client-facing document: formatting, branding, and sending. The Proper Tool does both: it generates a quantity-based estimate from drawings or descriptions and outputs a professional, branded PDF quote ready to send to the client.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there free building estimating software for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month. Fully free tools exist but typically lack UK-specific cost data, quantity takeoff, or professional PDF export, making them unsuitable for accurate residential estimates.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Layers,
    title: "Quantity-based cost build-ups",
    description: "Each line item carries a quantity, unit, and unit rate. The AI derives quantities from your description or from uploaded drawings: floor areas, linear metres, item counts.",
  },
  {
    icon: PoundSterling,
    title: "UK labour and materials rates",
    description: "462 rated tasks across 31 trade categories with current UK market rates. Override any rate with your own figure and your rate always takes priority.",
  },
  {
    icon: SlidersHorizontal,
    title: "Labour / materials / plant split",
    description: "Every line item carries a labour, materials, and plant cost breakdown. Category-level split rules calibrated to UK residential construction norms.",
  },
  {
    icon: FileText,
    title: "Reads architect's drawings",
    description: "Upload PDFs of architect's plans or structural drawings. The tool extracts beam sizes, spans, floor areas, and dimensions to improve estimate accuracy.",
  },
  {
    icon: BarChart3,
    title: "Trade-section breakdowns",
    description: "31 categories: groundworks, steelwork, carpentry, plastering, roofing, M&E, drainage, landscaping, and more. Totals grouped by trade section.",
  },
  {
    icon: BookOpen,
    title: "Scope completeness check",
    description: "Built-in expert validation flags missing dependencies: skip hire for demolition, tanking for a wet room, fire protection for exposed steelwork.",
  },
];

const COMPARISON_ROWS = [
  { aspect: "Quantity takeoff", manual: "Manual measurement required", software: "AI-derived from description or drawings" },
  { aspect: "Rate database", manual: "Build and maintain yourself", software: "462 UK tasks, auto-maintained" },
  { aspect: "L/M/P split", manual: "Manual calculation", software: "Auto-split per category rule" },
  { aspect: "Scope gaps", manual: "Easy to miss items", software: "Flagged by expert validation" },
  { aspect: "Time per estimate", manual: "2–4 hours", software: "Under 10 minutes" },
  { aspect: "PDF output", manual: "Manual formatting", software: "Auto-generated, branded" },
];

const FAQS = [
  {
    q: "What is the best estimating software for builders?",
    a: "For UK residential builders, The Proper Tool is purpose-built estimating software: a 31-category UK cost library with 462 rated tasks, AI-generated quantity-based cost build-ups from drawings or descriptions, labour/materials/plant splits, and professional PDF output. Plans start at £29/month with a 14-day free trial.",
  },
  {
    q: "What is building estimating software?",
    a: "Building estimating software automates the process of producing a cost estimate for a construction project. It combines a database of UK labour, materials, and plant rates with a process for deriving quantities from drawings or descriptions. The result is a fully itemised cost build-up that reflects the actual scope of the project. The Proper Tool is building estimating software designed specifically for UK residential construction.",
  },
  {
    q: "Can The Proper Tool produce quantity-based estimates from drawings?",
    a: "Yes. The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit (m², m, nr), and unit rate. When you upload architect's drawings or structural plans, the tool extracts floor areas, beam sizes, spans, and dimensions, using them to derive quantities more accurately than a description alone.",
  },
  {
    q: "Does building estimating software include labour and materials costs?",
    a: "Yes. Every line item in The Proper Tool carries a labour cost, materials cost, and plant cost breakdown. The split is derived from a 31-category rule set calibrated to UK residential construction norms. You can see the breakdown per item and per trade section.",
  },
  {
    q: "How does estimating software for builders differ from quoting software?",
    a: "Estimating software focuses on the cost build-up: deriving accurate quantities, applying labour and materials rates, and producing a detailed itemised estimate. Quoting software focuses on the client-facing document. The Proper Tool does both: it generates a quantity-based estimate from drawings or descriptions and outputs a professional, branded PDF quote ready to send.",
  },
  {
    q: "Is there free building estimating software for UK builders?",
    a: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features, no credit card required. After the trial, plans start from £29/month.",
  },
];

export default function EstimatingSoftwareForBuildersPage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Estimating Software for Builders | The Proper Tool"
        description="Estimating software for builders and building estimating software for UK residential construction. Quantity-based cost build-ups from drawings, UK labour and materials rates, 31 trade categories. From £29/mo, 14-day free trial."
        canonical="/estimating-software-for-builders"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Estimating Software for Builders</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Estimating Software for Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-4 max-w-3xl mx-auto">
              Accurate cost build-ups from drawings and descriptions, structured like a <Link href="/glossary#bill-of-quantities" className="text-primary underline underline-offset-2 hover:text-primary/80">bill of quantities</Link>, generated in under 10 minutes with UK labour and materials rates across 31 trade categories.
            </p>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
              The <strong className="text-foreground">building estimating software</strong> built specifically for UK residential construction, not adapted from a generic platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/construction-estimating-software">See estimating features</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* What good building estimating software does */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What good building estimating software does</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Under-pricing comes from missed items and wrong quantities, not wrong rates. Good estimating software addresses all three.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-muted/30 rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">From drawings to estimate in three steps</h2>
            <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
              No spreadsheet setup. No manual rate maintenance. Upload your drawings, describe the job, and get a full cost build-up.
            </p>
            <div className="space-y-8">
              {[
                {
                  step: "01",
                  title: "Upload drawings or describe the project",
                  body: "Upload architect's drawings or structural plans as PDFs, attach site photos, or type a plain-text project description. The Proper Tool reads all three, extracting beam sizes, floor areas, and dimensions from drawings to improve quantity accuracy.",
                },
                {
                  step: "02",
                  title: "AI generates a quantity-based cost build-up",
                  body: "The AI produces a fully itemised estimate: every trade, every line item, with quantities (m², m, nr), unit rates from the UK cost library, and a labour/materials/plant cost split per item. All 31 trade categories are considered against the project scope.",
                },
                {
                  step: "03",
                  title: "Review, adjust, and export",
                  body: "Review every line: edit quantities, swap rates, add or remove items. Apply or dismiss expert validation flags. Export a professionally formatted PDF with your company branding, full scope of works, trade-section totals, and cost breakdown.",
                },
              ].map((s) => (
                <div key={s.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-primary/20 flex-shrink-0 w-12">{s.step}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Manual vs software comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Manual estimating vs building estimating software</h2>
              <p className="text-lg text-muted-foreground">The hidden cost of manual estimating adds up with every job.</p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Aspect</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Manual / Excel</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.aspect}</td>
                      <td className="px-5 py-4 text-sm text-muted-foreground">
                        <span className="flex items-start gap-2">
                          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          {row.manual}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm text-green-600 dark:text-green-400 font-medium">
                        <span className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          {row.software}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="pricing">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, transparent pricing</h2>
              <p className="text-lg text-muted-foreground">Start with a 14-day free trial. No credit card required.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {([
                { name: "Lite", price: "£29", desc: "For sole traders", features: ["10 AI auto-estimates/mo", "Unlimited template & manual quotes", "Professional PDF export", "Full 31-category cost library"], highlight: false },
                { name: "Starter", price: "£59", desc: "For growing businesses", features: ["25 AI auto-estimates/mo", "Everything in Lite", "Pricing Intelligence", "Expert validation pipeline", "Regional pricing"], highlight: true },
                { name: "Pro", price: "£99", desc: "For high-volume builders", features: ["50 AI auto-estimates/mo", "Everything in Starter", "Drawing analysis", "Learned rate adjustments"], highlight: false },
              ] as { name: string; price: string; desc: string; features: string[]; highlight: boolean }[]).map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-xl border p-6 flex flex-col ${plan.highlight ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                >
                  <div className="mb-4">
                    <p className="font-bold text-lg">{plan.name}</p>
                    <p className="text-3xl font-bold mt-1">{plan.price}<span className="text-base font-normal text-muted-foreground">/mo</span></p>
                    <p className="text-sm text-muted-foreground mt-1">{plan.desc}</p>
                  </div>
                  <ul className="space-y-2 flex-1 mb-6">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant={plan.highlight ? "default" : "outline"} asChild className="w-full">
                    <Link href="/login">Start free trial</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/construction-estimating-software", label: "Construction Estimating Software" },
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/blog/ai-quoting-software-uk-builders", label: "AI Quoting Software for Builders: Is It Worth It?" },
                { href: "/blog/what-to-include-in-builders-quote", label: "What to Include in a Builder's Quote" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Stop estimating in spreadsheets</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Proper Tool generates a fully itemised, quantity-based building estimate in under 10 minutes, with UK rates, L/M/P splits, and expert scope validation built in.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">Plans from £29/mo · No credit card required</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
